'use client';

import Box from '@mui/material/Box';
import { alpha, keyframes, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';

export type AstorMode = 'driving' | 'reviewing' | 'idle' | 'paused' | 'error';

export interface AstorModeIndicatorProps {
  /** Current Astor mode. Drives the dot color, pulse, and default label. */
  mode: AstorMode;
  /** Override the auto-generated label (e.g. supply your own copy with i18n). */
  label?: ReactNode;
  /**
   * Optional confidence percentage (0–100). When provided, renders as " · conf NN%" after the
   * label.
   */
  confidence?: number;
  /** Click handler — when provided, the indicator becomes a button (e.g. opens Astor chat). */
  onClick?: () => void;
  /** Sx overrides. */
  sx?: SxProps<Theme>;
  /** Aria label override. Falls back to a sensible composed label. */
  ariaLabel?: string;
}

const pulseAnim = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.5; transform: scale(1.4); }
`;

const MODE_DEFAULTS: Record<
  AstorMode,
  { palette: 'success' | 'primary' | 'warning' | 'error' | 'info'; label: string; pulse: boolean }
> = {
  driving: { palette: 'success', label: 'Astor driving', pulse: true },
  reviewing: { palette: 'primary', label: 'Astor reviewing', pulse: true },
  idle: { palette: 'info', label: 'Astor idle', pulse: false },
  paused: { palette: 'warning', label: 'Astor paused', pulse: false },
  error: { palette: 'error', label: 'Astor error', pulse: false },
};

export function AstorModeIndicator({ mode, label, confidence, onClick, sx, ariaLabel }: AstorModeIndicatorProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const def = MODE_DEFAULTS[mode];
  const color = theme.palette[def.palette].main;

  const interactive = Boolean(onClick);
  const text = label ?? def.label;
  const confSuffix =
    typeof confidence === 'number' && Number.isFinite(confidence)
      ? ` · conf ${Math.round(Math.max(0, Math.min(100, confidence)))}%`
      : '';
  const composedAria = ariaLabel ?? `${typeof text === 'string' ? text : def.label}${confSuffix}`;

  return (
    <Box
      component={interactive ? 'button' : 'div'}
      {...(interactive ? { type: 'button' as const, onClick } : {})}
      aria-label={composedAria}
      sx={composeSx(
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.875,
          height: 28,
          px: 1.25,
          borderRadius: '14px',
          backgroundColor: alpha(color, isDark ? 0.16 : 0.1),
          border: `1px solid ${alpha(color, isDark ? 0.4 : 0.3)}`,
          color: isDark ? theme.palette[def.palette].light : theme.palette[def.palette].dark,
          fontFamily: 'inherit',
          cursor: interactive ? 'pointer' : 'default',
          transition: 'background-color 120ms ease, border-color 120ms ease',
          ...(interactive && {
            '&:hover': {
              backgroundColor: alpha(color, isDark ? 0.22 : 0.14),
            },
            '&:focus-visible': {
              outline: `2px solid ${color}`,
              outlineOffset: 2,
            },
          }),
        },
        sx,
      )}
    >
      <Box
        aria-hidden
        sx={{
          position: 'relative',
          width: 8,
          height: 8,
          flexShrink: 0,
        }}
      >
        {def.pulse && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              backgroundColor: color,
              opacity: 0.6,
              animation: `${pulseAnim} 1.8s ease-in-out infinite`,
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                opacity: 0.4,
              },
            }}
          />
        )}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: color,
          }}
        />
      </Box>
      <Typography
        component="span"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: 1,
          color: 'inherit',
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {text}
        {confSuffix && (
          <Box component="span" sx={{ color: 'inherit', opacity: 0.85, ml: 0.25 }} aria-hidden>
            {confSuffix}
          </Box>
        )}
      </Typography>
    </Box>
  );
}

AstorModeIndicator.displayName = 'AstorModeIndicator';

export default AstorModeIndicator;
